import React, { useEffect, useState } from 'react'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { useNavigate } from 'react-router-dom'
import { Apps } from '@mui/icons-material'
import { Grid, IconButton } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'

const StorageConfig = (props) => {
  const { deviceId, device } = props
  const [storage, setStorage] = useState({})
  const [loadStorage, setLoadStorage] = useState(true)
  const [isMTC, setIsMTC] = useState(device?.storageTypes?.at(0)?.code === 'MTC')
  const [defaultMorphology, setDefaultMorphology] = useState(null)
  const { t_ } = useSmartTranslation()
  const navigate = useNavigate()

  const fetchFunction = async () => {
    return await apiGet('shelveStorageConfigurations/' + deviceId)
  }

  const readOnlyStorageFunctionCondition = (row) => {
    let isReadOnly = false
    if (Array.isArray(row.storageFunctions)) {
      row.storageFunctions.forEach((storageFunction) => {
        if (storageFunction.storageFunctionMixed) {
          isReadOnly = true
        }
      })
    } else {
      if (row?.storageFunctions?.storageFunctionMixed !== undefined && row?.storageFunctions?.storageFunctionMixed === true) {
        isReadOnly = true
      }
    }
    return isReadOnly
  }

  const readOnlyTechnologyCondition = (row) => {
    let isReadOnly = false
    if (Array.isArray(row.technologies)) {
      row.technologies.forEach((technology) => {
        if (technology?.technologyMixed) {
          isReadOnly = true
        }
      })
    } else {
      if (row?.technologies?.technologyMixed !== undefined && row?.technologies?.technologyMixed === true) {
        isReadOnly = true
      }
    }
    return isReadOnly
  }

  useEffect(() => {
    if (device?.storageTypes?.at(0)?.code === 'MTC') {
      setIsMTC(true)
    }
    if (device?.storageTypes?.at(0)?.code !== 'MTC') {
      setIsMTC(false)
    }
  }, [device])

  useEffect(() => {
    if (isMTC) {
      apiGet('morphologies').then((data) => {
        const container = data.find((m) => m.code === 'CONTAINER')
        if (container) setDefaultMorphology(container)
      })
    } else {
      setDefaultMorphology(null)
    }
  }, [isMTC])

  useEffect(() => {
    const fetchStorage = async () => {
      const st = await apiGet('storages/byDeviceId/' + deviceId)
      setStorage(st)
    }

    if (loadStorage) {
      fetchStorage()
      setLoadStorage(false)
    }
  }, [storage, deviceId, loadStorage])

  const actionShelveConfigurationTable = {
    edit: {
      fetchFunction: (editRow) => {
        let apiPutParam = { shelveStorageConfiguration: editRow }
        if (!Array.isArray(editRow.storageFunctions)) {
          apiPutParam = { ...apiPutParam, storageFunctions: null, updateStorageFunctions: !editRow.storageFunctions.isStorageFunctionMixed }
        } else {
          apiPutParam = Object.assign({ ...apiPutParam, storageFunctions: editRow.storageFunctions, updateStorageFunctions: true })
        }
        if (!Array.isArray(editRow.technologies)) {
          apiPutParam = { ...apiPutParam, technologies: null, updateTechnologies: !editRow.technologies.isTechnologyMixed }
        } else {
          apiPutParam = Object.assign({ ...apiPutParam, technologies: editRow.technologies, updateTechnologies: true })
        }
        return apiPut('shelveStorageConfigurationsAndStorageFunctions/', apiPutParam).then(() => { setLoadStorage(true) })
      }
    },
    create: !isMTC || storage?.shelveStorageConfigurations?.length === 0
      ? {
          fetchFunction: (newShelf) => {
            if (Object.keys(storage).length > 0) {
              const serializedShelf = {
                ...newShelf,
                storageId: storage.id
              }

              const copyStorage = { ...storage }
              copyStorage.shelveStorageConfigurations.push(serializedShelf)
              setStorage(copyStorage)
              const apiPutParam = Object.assign({ storageFunctions: serializedShelf.storageFunctions, technologies: serializedShelf.technologies, updateStorageFunctions: true, updateTechnologies: true, shelveStorageConfiguration: serializedShelf })
              return apiPut('shelveStorageConfigurationsAndStorageFunctions', apiPutParam).then(() => { setLoadStorage(true) })
            }
          }
        }
      : false,
    custom: [
      {
        name: 'Shelve Positions',
        showOnNew: 'false',
        disabled: isMTC,
        render: (row) => {
          return (
            <IconButton>
              <Apps />
            </IconButton>
          )
        },
        onClick: (shelve) => {
          navigate('/configuration/assets/' + deviceId + '/shelve/' + shelve.id)
        }
      }
    ]
  }

  const getShelveByDefault = () => {
    let shelveNumber = 1
    if (storage.shelveStorageConfigurations !== undefined && storage.shelveStorageConfigurations.length !== 0) {
      shelveNumber = Math.max(...storage.shelveStorageConfigurations.map(shelveStorageConfiguration => shelveStorageConfiguration.shelve)) + 1
    }
    return shelveNumber
  }

  const columnsInfoShelves = [
    {
      name: t_('Shelf'),
      field: 'shelve',
      readOnly: (rowData) => (rowData?.id === undefined && isMTC) || (true && rowData?.id !== undefined),
      defaultNewValue: isMTC ? 1 : getShelveByDefault(),
      unique: true,
      numeric: true
    },
    {
      name: t_('Rows'),
      field: 'numRows',
      defaultNewValue: 1,
      readOnly: (rowData) => (rowData?.id === undefined && isMTC) || (true && rowData?.id !== undefined),
      numeric: true
    },
    {
      name: t_('Columns'),
      field: 'numColumns',
      defaultNewValue: 1,
      readOnly: true,
      numeric: true
    },
    {
      name: t_('Width'),
      field: 'width',
      defaultNewValue: 120
    },
    {
      name: t_('Length'),
      field: 'length',
      defaultNewValue: 120
    },
    {
      name: t_('Zero X'),
      field: 'zeroX',
      defaultNewValue: 0,
      numeric: {
        default: 0
      }
    },
    {
      name: t_('Zero Y'),
      field: 'zeroY',
      defaultNewValue: 0,
      numeric: {
        default: 0
      }
    },
    {
      name: t_('Zero Z'),
      field: 'zeroZ',
      defaultNewValue: 0,
      numeric: {
        default: 0
      }
    },
    {
      name: t_('Morphology'),
      field: 'morphology.name',
      type: 'select',
      edit: {
        source: async () => {
          return await apiGet('morphologies')
        },
        id: 'id',
        field: 'name'
      },
      defaultNewValue: defaultMorphology,
      mandatory: true,
      readOnly: (rowData) => isMTC && (rowData?.id === undefined || true)
    },
    {
      name: t_('Function'),
      field: 'storageFunctions.code',
      type: 'smartMultiSelect',
      edit: {
        source: async () => {
          return await apiGet('storageFunctions')
        },
        id: 'id',
        field: 'code',
        optionLabel: (code) => systemValueLabel(t_, 'storage_function', code || '')
      },
      readOnly: (rowData) => {
        return readOnlyStorageFunctionCondition(rowData)
      }
    },
    {
      name: t_('Technology'),
      field: 'technologies.name',
      type: 'smartMultiSelect',
      edit: {
        source: async () => {
          return await apiGet('technologiesAvailableByDevice/' + deviceId)
        },
        id: 'id',
        field: 'name'
      },
      readOnly: (rowData) => {
        return readOnlyTechnologyCondition(rowData)
      }
    }
  ]
  return (
    <>
      <Grid container spacing={10}>
        <Grid item xs={12}>
          <SmartMateriaUITable
            title=''
            columns={columnsInfoShelves}
            dataFetch={fetchFunction}
            actions={actionShelveConfigurationTable}
            multipleOptions
          />
        </Grid>
      </Grid>
    </>
  )
}

export default StorageConfig
